import React, { useEffect, useState } from 'react';
import './i18n';
import { useTranslation } from 'react-i18next';

/**
 * Reusable verification popup.
 *
 * Displays either a single input box (SMS or email token) or both input boxes
 * when both SMS and email validation are required. Each field shows the masked
 * destination and a "Resend" link with a countdown.
 *
 * Props:
 *   open            - whether the popup is visible
 *   needsPhone      - show the SMS code input
 *   needsEmail      - show the email code input
 *   maskedPhone     - masked phone number to display
 *   maskedEmail     - masked email address to display
 *   smsResendDelay  - SMS resend countdown (seconds)
 *   emailResendDelay- email resend countdown (seconds)
 *   nonce           - change this value to force the countdowns to restart
 *                     (e.g. after a resend, even when the delay is unchanged)
 *   error           - optional error message
 *   onResend(scope) - called when a Resend link is clicked ('phone' | 'email')
 *   onCancel()      - called when Cancel is clicked
 *   onConfirm(phoneCode, emailCode) - called when Confirm is clicked
 */
function VerificationModal({
  open,
  needsPhone,
  needsEmail,
  maskedPhone,
  maskedEmail,
  smsResendDelay,
  emailResendDelay,
  nonce = 0,
  error,
  onResend,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [phoneCode, setPhoneCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [smsSecondsLeft, setSmsSecondsLeft] = useState(0);
  const [emailSecondsLeft, setEmailSecondsLeft] = useState(0);

  useEffect(() => {
    if (!open) {
      setPhoneCode('');
      setEmailCode('');
      setSmsSecondsLeft(0);
      setEmailSecondsLeft(0);
      return;
    }
    setSmsSecondsLeft(needsPhone ? smsResendDelay : 0);
    setEmailSecondsLeft(needsEmail ? emailResendDelay : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsPhone, needsEmail, smsResendDelay, emailResendDelay, nonce]);

  useEffect(() => {
    if (!open || smsSecondsLeft <= 0) return;
    const id = setInterval(() => setSmsSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [open, smsSecondsLeft]);

  useEffect(() => {
    if (!open || emailSecondsLeft <= 0) return;
    const id = setInterval(() => setEmailSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [open, emailSecondsLeft]);

  if (!open) return null;

  // Title reflects which verification channel(s) were requested:
  //   phone + email | phone only | email only
  const title = needsPhone && needsEmail
    ? t('We have to verify your phone and email')
    : needsPhone
      ? t('We have to verify your phone')
      : t('We have to verify your email');

  const handleConfirm = () => {
    onConfirm(phoneCode, emailCode);
  };

  return (
    <div className="verification-modal-backdrop">
      <div className="verification-modal">
        <h3>{title}</h3>

        {needsPhone && (
          <div className="verification-field">
            <label>
              {t('Enter a value from SMS we just sent to')} {maskedPhone}
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={phoneCode}
              placeholder={t('SMS code')}
              onChange={(e) => setPhoneCode(e.target.value)}
            />
            <button
              type="button"
              className="button is-link"
              disabled={smsSecondsLeft > 0}
              onClick={() => onResend('phone')}
            >
              {smsSecondsLeft > 0
                ? `${t("Didn't receive SMS? Resend")} ${smsSecondsLeft}s`
                : t("Didn't receive SMS? Resend")}
            </button>
          </div>
        )}

        {needsEmail && (
          <div className="verification-field">
            <label>
              {t('Enter a value from email we just sent to')} {maskedEmail}
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoFocus={!needsPhone}
              value={emailCode}
              placeholder={t('Email code')}
              onChange={(e) => setEmailCode(e.target.value)}
            />
            <button
              type="button"
              className="button is-link"
              disabled={emailSecondsLeft > 0}
              onClick={() => onResend('email')}
            >
              {emailSecondsLeft > 0
                ? `${t("Didn't receive email? Resend")} ${emailSecondsLeft}s`
                : t("Didn't receive email? Resend")}
            </button>
          </div>
        )}

        {error && <div className="error-text">{error}</div>}

        <div className="verification-actions">
          <button type="button" className="button is-outline" onClick={onCancel}>
            {t('Cancel')}
          </button>
          <button type="button" className="button is-primary" onClick={handleConfirm}>
            {t('Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerificationModal;